import {
	Button,
	Box,
	Typography,
	Table,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	// Card, CardContent removed - not used
	TableBody,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { CheckCircleOutline } from '@mui/icons-material';
import PastDueLogo from '../../assets/images/pastDueLogo.png';
import { NavLink } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getCategorizedToDos, DeleteToDo } from 'core/API/todo';
import { useWorkplaceUser } from 'module/documents/utils';
// TodoWordIcon removed - not used
import { useEffect, useState } from 'react';
import { useDocumentContext } from 'module/documents/context';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
// Edit icon not used here
import styles from './Todo.module.scss';
import {
	formatDateMMDDYYYY,
	formatDateTime12Hour,
	// validateDateNotPast,
	// isSameDay,
	getTodayISO,
} from 'shared/utils/dateUtils';

const Todo = () => {
	const {
		selectedEmployee: _selectedEmployee,
		startDate,
		setStartDate,
		endDate: _endDate,
		setEndDate: _setEndDate,
	} = useDocumentContext();
	const workplace = useWorkplaceUser();
	const workplaceId = Number(localStorage.getItem('workplace-id')) || workplace.data.id;

	const queryClient = useQueryClient();
	const { showToast } = useToastContext();

	// Delete confirmation dialog state
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [todoToDelete, setTodoToDelete] = useState<any>(null);

	// Single query for categorized todos
	const {
		data: categorizedTodosData,
		isLoading,
		error,
	} = useQuery(['todos-categorized', workplaceId], () => getCategorizedToDos({ workplaceId, page: 1, itemsPerPage: 10 }), {
		enabled: !!workplaceId,
		refetchOnMount: 'always',
		refetchOnWindowFocus: false,
		staleTime: 0,
	});

	// console.log('mytodos', myTodoList, todoList);
	// Permission checks for To-Do module (call hooks before any early returns)
	const {
		hasModuleViewPermission,
		hasModuleEditPermission,
		hasModuleDeletePermission,
		hasModuleExecutePermission,
	} = usePermissionCheck();
	const canViewToDoMy = hasModuleViewPermission('To-Do - My');
	const canViewToDoAll = hasModuleViewPermission('To-Do - All');
	const canExecuteToDo = hasModuleExecutePermission('To-Do - My') || hasModuleExecutePermission('To-Do - All');

	// Determine edit capability:
	// If user can view 'To-Do - All' allow edit when they have edit on either label
	// If user can only view 'To-Do - My' allow edit only when they have edit on 'To-Do - My'
	let canEditToDo = false;
	if (canViewToDoAll) {
		canEditToDo = hasModuleEditPermission('To-Do - All') || hasModuleEditPermission('To-Do - My');
	} else if (canViewToDoMy) {
		canEditToDo = hasModuleEditPermission('To-Do - My');
	} else {
		canEditToDo = false;
	}

	// Determine delete capability similarly to edit capability
	let canDeleteToDo = false;
	if (canViewToDoAll) {
		canDeleteToDo = hasModuleDeletePermission('To-Do - All') || hasModuleDeletePermission('To-Do - My');
	} else if (canViewToDoMy) {
		canDeleteToDo = hasModuleDeletePermission('To-Do - My');
	} else {
		canDeleteToDo = false;
	}

	// Delete todo mutation
	const { mutate: deleteTodo, isLoading: isDeleting } = useMutation(
		(id: number) => DeleteToDo(id),
		{
			onSuccess: () => {
				// Refetch todos after successful deletion
				queryClient.invalidateQueries(['todos-categorized', workplaceId]);
				setDeleteDialogOpen(false);
				setTodoToDelete(null);
				showToast('success', 'Todo deleted successfully!');
			},
			onError: (error) => {
				console.error('Failed to delete todo:', error);
				setDeleteDialogOpen(false);
				setTodoToDelete(null);
				showToast('error', 'Failed to delete todo. Please try again.');
			},
		}
	);

	useEffect(() => {
		if (!startDate) {
			const storedDate = localStorage.getItem('todo-selected-date');
			const nextDate = storedDate || getTodayISO();
			setStartDate(nextDate);
			if (!storedDate) {
				localStorage.setItem('todo-selected-date', nextDate);
			}
		}
	}, [startDate, setStartDate]);

	if (isLoading) {
		return (
			<Box className={styles.loadingState}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) return <div>Error fetching To-Dos</div>;

	// Extract data from categorized API response (arrays are directly on root object)
	const myTodos = Array.isArray(categorizedTodosData?.my_todos) ? categorizedTodosData.my_todos : [];
	const outstandingTodos = Array.isArray(categorizedTodosData?.outstanding_todos) ? categorizedTodosData.outstanding_todos : [];
	const pastDueTodos = Array.isArray(categorizedTodosData?.past_due_todos) ? categorizedTodosData.past_due_todos : [];
	const completedTodos = Array.isArray(categorizedTodosData?.completed_todos) ? categorizedTodosData.completed_todos : [];

	const getUserFullName = (user: any): string => {
		// Handle null or undefined user
		if (!user) {
			return 'Unknown User';
		}
		const parts = [user?.firstName, user?.middleName, user?.lastName].filter(
			(p: string | undefined) => !!p && p.trim() !== ''
		);
		return parts.join(' ') || 'Unknown User';
	};

	// parseApiPathId removed - not needed

	// Helper function to format date with time as MM/DD/YYYY HH:MM:SS AM/PM
	const formatTodoDateWithTime = (todo: any): string => {
		const dueDateRaw = todo.due_date ?? todo.dueDate;
		const dueTime = todo.dueTime;
		return formatDateTime12Hour(dueDateRaw, dueTime);
	};

	// Helper function to get assignment display value
	const getAssignmentDisplay = (todo: any): string => {
		// Check if user exists and is not null
		if (todo?.user && todo.user !== null) {
			return getUserFullName(todo.user);
		} else if (todo?.assignedGroup) {
			return todo.assignedGroup.label || 'Group Assignment';
		} else if (todo?.assigned) {
			return todo.assigned;
		} else {
			return 'Unassigned';
		}
	};

	// Helper to get a robust description across varying API shapes
	const getDescriptionDisplay = (todo: any): string => {
		const candidates = [
			todo?.description,
			todo?.label,
			todo?.name,
			todo?.title,
			todo?.note?.message,
			todo?.note?.description,
			typeof todo?.note === 'string' ? todo?.note : undefined,
		];
		const val = candidates.find((v: any) => typeof v === 'string' && v.trim() !== '');
		return (val as string) || 'N/A';
	};

	const getCategoryDisplay = (todo: any): string => {
		const category = todo?.category;
		if (!category) return 'Uncategorized';
		if (typeof category === 'string') return category;
		return category?.label || category?.name || category?.title || 'Uncategorized';
	};

	// Delete handlers
	const handleDeleteClick = (todo: any) => {
		setTodoToDelete(todo);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = () => {
		if (todoToDelete) {
			deleteTodo(todoToDelete.id);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setTodoToDelete(null);
	};

	// currentUserId for permission filtering
	const currentUserId = Number(localStorage.getItem('userId'));

	// Filter todos based on permissions (security checks remain)
	// My Outstanding: show my_todos from API
	const outstandingMine = myTodos;

	// All Outstanding: show outstanding_todos from API, filtered by permissions
	const allOutstandingToShow = canViewToDoAll
		? outstandingTodos
		: canViewToDoMy
			? outstandingMine
			: [];

	// Completed: show completed_todos from API, filtered by permissions
	const completedToShow = canViewToDoAll
		? completedTodos
		: canViewToDoMy
			? completedTodos.filter((todo: any) => {
					const userId = todo.user?.id;
					return userId === currentUserId;
				})
			: [];

	// Past Due: show past_due_todos from API, filtered by permissions
	const overdueToShow = canViewToDoAll
		? pastDueTodos
		: canViewToDoMy
			? pastDueTodos.filter((todo: any) => {
					const userId = todo.user?.id;
					return userId === currentUserId;
				})
			: [];

	// Function to render "No Data" message
	const renderNoData = () => (
		<Box className={styles.noRecordsFound}>
			<Typography variant="h6" color="textSecondary">
				No records found
			</Typography>
		</Box>
	);

	return (
		<Box p={2} sx={{ backgroundColor: '#f0f6fc', minHeight: '100vh' }}>
			{/* Header */}
			<Box display="flex" justifyContent="end" alignItems="center" mb={2}>
				{canExecuteToDo && (
					<Button
						component={NavLink}
						to={ROUTES.ADD_TO_DO}
						style={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: 'white',
							padding: '5px 15px',
							borderRadius: '4px',
							fontSize: '14px',
							cursor: 'pointer',
							textTransform: 'capitalize',
							textDecoration: 'none',
						}}
					>
						Add
					</Button>
				)}
			</Box>

			{/* Outstanding - Mine */}
			<Typography variant="h6" bgcolor={'#fff'} padding={'10px'} gutterBottom>
				My Outstanding To-Dos
			</Typography>
			<TableContainer
				style={{ height: 'max-content', maxHeight: '300px' }}
				component={Paper}
				sx={{ mb: 2 }}
			>
				<Table sx={{ tableLayout: 'fixed', width: '100%' }}>
					<TableHead>
						<TableRow style={{ backgroundColor: '#f5f5f5' }}>
							<TableCell sx={{ width: '22%' }}>
								<b>Description</b>
							</TableCell>
							<TableCell sx={{ width: '18%' }}>
								<b>Assigned</b>
							</TableCell>
							<TableCell sx={{ width: '14%' }}>
								<b>Category</b>
							</TableCell>
							<TableCell sx={{ width: '9%' }}>
								<b>Priority</b>
							</TableCell>
							<TableCell sx={{ width: '9%' }}>
								<b>Status</b>
							</TableCell>
							<TableCell sx={{ width: '18%' }}>
								<b>Due Date</b>
							</TableCell>
							<TableCell sx={{ width: '10%', whiteSpace: 'nowrap' }}>
								<b>Action</b>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{!canViewToDoMy ? (
							<TableRow>
								<TableCell colSpan={7} align="center">
									You do not have permissions to view this!
								</TableCell>
							</TableRow>
						) : outstandingMine.length > 0 ? (
							outstandingMine.map((todo: any) => (
								<TableRow key={todo.id}>
									<TableCell>
										<NavLink
											to={ROUTES.TO_DO_ID.replace(':id', (todo.id || '').toString())}
											style={{ textDecoration: 'none', color: 'inherit' }}
										>
											{getDescriptionDisplay(todo)}
										</NavLink>
									</TableCell>
									<TableCell>{getAssignmentDisplay(todo)}</TableCell>
									<TableCell>{getCategoryDisplay(todo)}</TableCell>
									<TableCell>{todo.priority?.name ?? todo.priority?.label}</TableCell>
									<TableCell>{todo.status?.name ?? todo.status?.label}</TableCell>
									<TableCell>{formatTodoDateWithTime(todo)}</TableCell>
									<TableCell sx={{ whiteSpace: 'nowrap' }}>
										<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.25 }}>
										{canEditToDo ? (
											<NavLink to={ROUTES.EDIT_TO_DO.replace(':id', todo.id.toString())}>
												<IconButton sx={{ color: 'rgb(68, 99, 136)' }}>
													<EditIcon style={{ fontSize: '20px' }} />
												</IconButton>
											</NavLink>
										) : (
											<IconButton sx={{ color: 'grey.400' }} disabled>
												<EditIcon style={{ fontSize: '20px' }} />
											</IconButton>
										)}
										<IconButton
											sx={{ color: canDeleteToDo ? 'red' : 'grey.400' }}
											onClick={() => handleDeleteClick(todo)}
											disabled={isDeleting || !canDeleteToDo}
										>
											<DeleteIcon style={{ fontSize: '20px' }} />
										</IconButton>
									</Box>
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={7}>{renderNoData()}</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Outstanding - All */}
			<Typography variant="h6" bgcolor={'#fff'} padding={'10px'} gutterBottom>
				All Outstanding To-Dos
			</Typography>
			<TableContainer
				style={{ height: 'max-content', maxHeight: '300px' }}
				component={Paper}
				sx={{ mb: 2 }}
			>
				<Table sx={{ tableLayout: 'fixed', width: '100%' }}>
					<TableHead>
						<TableRow style={{ backgroundColor: '#f5f5f5' }}>
							{/* <TableCell><b>ID</b></TableCell> */}
							<TableCell sx={{ width: '22%' }}>
								<b>Description</b>
							</TableCell>
							<TableCell sx={{ width: '18%' }}>
								<b>Assigned</b>
							</TableCell>
							<TableCell sx={{ width: '14%' }}>
								<b>Category</b>
							</TableCell>
							<TableCell sx={{ width: '9%' }}>
								<b>Priority</b>
							</TableCell>
							<TableCell sx={{ width: '9%' }}>
								<b>Status</b>
							</TableCell>
							<TableCell sx={{ width: '18%' }}>
								<b>Due Date</b>
							</TableCell>
							<TableCell sx={{ width: '10%', whiteSpace: 'nowrap' }}>
								<b>Action</b>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{!(canViewToDoAll || canViewToDoMy) ? (
							<TableRow>
								<TableCell colSpan={7} align="center">
									You do not have permissions to view this!
								</TableCell>
							</TableRow>
						) : allOutstandingToShow.length > 0 ? (
							allOutstandingToShow.map((todo: any) => (
								<TableRow key={todo.id}>
									{/* <TableCell>{todo.id}</TableCell> */}
									<TableCell>
										<NavLink
											to={ROUTES.TO_DO_ID.replace(':id', (todo.id || '').toString())}
											style={{ textDecoration: 'none', color: 'inherit' }}
										>
											{getDescriptionDisplay(todo)}
										</NavLink>
									</TableCell>
									<TableCell>{getAssignmentDisplay(todo)}</TableCell>
									<TableCell>{getCategoryDisplay(todo)}</TableCell>
									<TableCell>{todo.priority?.name ?? todo.priority?.label}</TableCell>
									<TableCell>{todo.status?.name ?? todo.status?.label}</TableCell>
									<TableCell>{formatTodoDateWithTime(todo)}</TableCell>
									<TableCell sx={{ whiteSpace: 'nowrap' }}>
										<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.25 }}>
										{canEditToDo ? (
											<NavLink to={ROUTES.EDIT_TO_DO.replace(':id', todo.id.toString())}>
												<IconButton sx={{ color: 'rgb(68, 99, 136)' }}>
													<EditIcon style={{ fontSize: '20px' }} />
												</IconButton>
											</NavLink>
										) : (
											<IconButton sx={{ color: 'grey.400' }} disabled>
												<EditIcon style={{ fontSize: '20px' }} />
											</IconButton>
										)}
										<IconButton
											sx={{ color: canDeleteToDo ? 'red' : 'grey.400' }}
											onClick={() => handleDeleteClick(todo)}
											disabled={isDeleting || !canDeleteToDo}
										>
											<DeleteIcon style={{ fontSize: '20px' }} />
										</IconButton>
										{/* <NavLink
                                            to={`/manager/todo/get/${todo.id}`}
                                            style={{ textDecoration: 'none', color: 'blue' }}
                                        >
                                            View
                                        </NavLink> */}
									</Box>
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={7}>{renderNoData()}</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Completed and Overdue Tables */}
			<Box
				sx={{
					width: '100%',
					display: 'flex',
					justifyContent: 'space-between',
					gap: 2,
					pb: 2,
				}}
			>
				{/* Completed To-Do Table */}
				<TableContainer
					component={Paper}
					sx={{
						width: '50%',
						maxHeight: '250px',
						backgroundColor: '#fff',
						borderRadius: 2,
						boxShadow: 2,
					}}
				>
					<Typography variant="h6" bgcolor={'#fff'} padding={'10px'}>
						Completed To-Dos
					</Typography>
					<Table sx={{ tableLayout: 'fixed', width: '100%' }}>
						<TableHead>
							<TableRow style={{ backgroundColor: '#f5f5f5' }}>
								<TableCell sx={{ width: '22%' }}>
									<b>Description</b>
								</TableCell>
								<TableCell sx={{ width: '18%' }}>
									<b>Assigned</b>
								</TableCell>
								<TableCell sx={{ width: '14%' }}>
									<b>Category</b>
								</TableCell>
								<TableCell sx={{ width: '9%' }}>
									<b>Priority</b>
								</TableCell>
								<TableCell sx={{ width: '12%' }}>
									<b>Completed Date</b>
								</TableCell>
								<TableCell sx={{ width: '25%' }}>
									<b>On Time / Overdue</b>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{!(canViewToDoAll || canViewToDoMy) ? (
								<TableRow>
									<TableCell colSpan={6} align="center">
										You do not have permissions to view this!
									</TableCell>
								</TableRow>
							) : completedToShow.length > 0 ? (
								completedToShow.map((todo: any) => {
									return (
										<TableRow key={todo.id}>
											<TableCell>
												<NavLink
													to={ROUTES.TO_DO_ID.replace(':id', (todo.id || '').toString())}
													style={{ textDecoration: 'none', color: 'inherit' }}
												>
													{getDescriptionDisplay(todo)}
												</NavLink>
											</TableCell>
											<TableCell>{getAssignmentDisplay(todo)}</TableCell>
											<TableCell>{getCategoryDisplay(todo)}</TableCell>
											<TableCell>{todo.priority?.name ?? todo.priority?.label}</TableCell>
											<TableCell>
												{todo.completedAt ? formatDateMMDDYYYY(todo.completedAt) : 'N/A'}
											</TableCell>
											<TableCell>
												{todo.pastDue ? (
													<img src={PastDueLogo} alt="past due" height="30px" />
												) : (
													<CheckCircleOutline sx={{ color: 'green', fontSize: '30px' }} />
												)}
											</TableCell>
										</TableRow>
									);
								})
							) : (
								<TableRow>
									<TableCell colSpan={6}>{renderNoData()}</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{/* Overdue To-Do Table */}
				<TableContainer
					component={Paper}
					sx={{
						width: '50%',
						maxHeight: '250px',
						backgroundColor: '#fff',
						borderRadius: 2,
						boxShadow: 2,
					}}
				>
					<Typography variant="h6" bgcolor={'#fff'} padding={'10px'}>
						Past Due To-Dos
					</Typography>
					<Table sx={{ tableLayout: 'fixed', width: '100%' }}>
						<TableHead>
							<TableRow style={{ backgroundColor: '#f5f5f5' }}>
								<TableCell sx={{ width: '22%' }}>
									<b>Description</b>
								</TableCell>
								<TableCell sx={{ width: '18%' }}>
									<b>Assigned</b>
								</TableCell>
								<TableCell sx={{ width: '14%' }}>
									<b>Category</b>
								</TableCell>
								<TableCell sx={{ width: '9%' }}>
									<b>Priority</b>
								</TableCell>
								<TableCell sx={{ width: '18%' }}>
									<b>Due Date</b>
								</TableCell>
								<TableCell sx={{ width: '10%', whiteSpace: 'nowrap' }}>
									<b>Action</b>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{!(canViewToDoAll || canViewToDoMy) ? (
								<TableRow>
									<TableCell colSpan={6} align="center">
										You do not have permissions to view this!
									</TableCell>
								</TableRow>
							) : overdueToShow.length > 0 ? (
								overdueToShow.map((todo: any) => (
									<TableRow key={todo.id}>
										<TableCell>
											<NavLink
												to={ROUTES.TO_DO_ID.replace(':id', (todo.id || '').toString())}
												style={{ textDecoration: 'none', color: 'inherit' }}
											>
												{getDescriptionDisplay(todo)}
											</NavLink>
										</TableCell>
										<TableCell>{getAssignmentDisplay(todo)}</TableCell>
										<TableCell>{getCategoryDisplay(todo)}</TableCell>
										<TableCell>{todo.priority?.name ?? todo.priority?.label}</TableCell>
										<TableCell>{formatTodoDateWithTime(todo)}</TableCell>
										<TableCell sx={{ whiteSpace: 'nowrap' }}>
											<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.25 }}>
											{canEditToDo ? (
												<NavLink to={ROUTES.EDIT_TO_DO.replace(':id', todo.id.toString())}>
													<IconButton sx={{ color: 'rgb(68, 99, 136)' }}>
														<EditIcon style={{ fontSize: '20px' }} />
													</IconButton>
												</NavLink>
											) : (
												<IconButton sx={{ color: 'grey.400' }} disabled>
													<EditIcon style={{ fontSize: '20px' }} />
												</IconButton>
											)}
											<IconButton
												sx={{ color: canDeleteToDo ? 'red' : 'grey.400' }}
												onClick={() => handleDeleteClick(todo)}
												disabled={isDeleting || !canDeleteToDo}
											>
												<DeleteIcon style={{ fontSize: '20px' }} />
											</IconButton>
										</Box>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={6}>{renderNoData()}</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleDeleteCancel}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
			>
				<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to delete the todo &ldquo;{todoToDelete?.description}&rdquo;? This
						action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteCancel} style={{ color: 'black' }}>
						Cancel
					</Button>
					<Button
						onClick={handleDeleteConfirm}
						style={{ backgroundColor: 'red', color: 'white' }}
						disabled={isDeleting}
					>
						{isDeleting ? 'Deleting...' : 'Delete'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default Todo;
